import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { loadBrandsRequest } from '../../../redux/brandsRedux';
import { loadPackagesRequest } from '../../../redux/packagesRedux';

import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

const BeerForm = ({ props, action, actionText, buttonText }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [packages, setPackages] = useState([]);
  const [brands, setBrands] = useState([]);

  const [brandId, setBrandId] = useState('');
  const [packageId, setPackageId] = useState('');

  const [materialNumber, setMaterialNumber] = useState('');

  const [description, setDescription] = useState('');

  const [shelfLife, setShelfLife] = useState('');

  const [palletQty, setPalletQty] = useState('');

  const [casHL, setCasHL] = useState('');

  const [validationAlert, setValidationAlert] = useState(false);

  const [submitError, setSubmitError] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const kegType = useMemo(() => detectKegType(description), [description]);

  useEffect(() => {
    async function loadFormOptions() {
      try {
        const packagesResponse = await dispatch(loadPackagesRequest());

        setPackages(packagesResponse?.data ?? []);

        const brandsResponse = await dispatch(loadBrandsRequest());

        setBrands(brandsResponse?.data ?? []);
      } catch (error) {
        setSubmitError('Nie udało się pobrać marek lub opakowań.');
      }
    }

    loadFormOptions();
  }, [dispatch]);

  useEffect(() => {
    const detectedKegType = detectKegType(props?.description);

    setBrandId(props?.brand?.id ?? props?.brandId ?? '');

    setPackageId(props?.package?.id ?? props?.packageId ?? '');

    setMaterialNumber(props?.materialNumber ?? '');

    setDescription(props?.description ?? '');

    setShelfLife(props?.shelfLife ?? '');

    setPalletQty(props?.palletQty ?? (detectedKegType ? 6 : ''));

    setCasHL(props?.casHL ?? '');

    setSubmitError(null);
    setValidationAlert(false);
  }, [props]);

  const brandOptions = useMemo(
    () =>
      brands.map((brand) => ({
        id: brand.id,
        label: brand.description,
      })),
    [brands],
  );

  const packageOptions = useMemo(
    () =>
      packages.map((packageItem) => ({
        id: packageItem.id,
        label: packageItem.description,
      })),
    [packages],
  );

  const selectedBrand =
    brandOptions.find((brand) => brand.id === brandId) ?? null;

  const selectedPackage =
    packageOptions.find((packageItem) => packageItem.id === packageId) ?? null;

  const handleCloseValidationAlert = () => {
    setValidationAlert(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitError(null);

    if (
      materialNumber === '' ||
      description.trim() === '' ||
      brandId === '' ||
      packageId === '' ||
      shelfLife === '' ||
      palletQty === '' ||
      casHL === ''
    ) {
      setValidationAlert(true);
      return;
    }

    const parsedMaterialNumber = Number.parseInt(materialNumber, 10);

    const parsedShelfLife = Number.parseInt(shelfLife, 10);

    const parsedPalletQty = Number.parseInt(palletQty, 10);

    const parsedCasHL = Number.parseFloat(
      String(casHL).trim().replace(',', '.'),
    );

    if (
      !Number.isInteger(parsedMaterialNumber) ||
      !Number.isInteger(parsedShelfLife) ||
      parsedShelfLife < 0 ||
      !Number.isInteger(parsedPalletQty) ||
      parsedPalletQty <= 0 ||
      !Number.isFinite(parsedCasHL) ||
      parsedCasHL <= 0
    ) {
      setSubmitError('Sprawdź poprawność wartości liczbowych.');

      return;
    }

    const data = {
      materialNumber: parsedMaterialNumber,

      description: description.trim(),

      brandId,
      packageId,

      shelfLife: parsedShelfLife,

      palletQty: parsedPalletQty,

      casHL: parsedCasHL,
    };

    setSubmitting(true);

    try {
      await action(data);
    } catch (error) {
      setSubmitError(getActionErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" fontWeight={700}>
        {actionText}
      </Typography>

      <Divider
        sx={{
          width: '100%',
          mb: 3,
          mt: 1,
          bgcolor: '#C09A53',
        }}
      />

      <Snackbar
        open={validationAlert}
        autoHideDuration={6000}
        onClose={handleCloseValidationAlert}
      >
        <Alert
          variant="filled"
          onClose={handleCloseValidationAlert}
          severity="error"
          sx={{ width: '100%' }}
        >
          <AlertTitle>Uwaga</AlertTitle>
          Uzupełnij wszystkie wymagane pola.
        </Alert>
      </Snackbar>

      {submitError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}

      {kegType && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Rozpoznano produkt <strong>{kegType}</strong>. Ilość na pełnej palecie
          została ustawiona na 6 KEG. Dla zakładu 8AAA pełny stack wynosi{' '}
          <strong>{getKegStackQuantity(kegType)}</strong> KEG.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t('beer.index')}
              variant="outlined"
              required
              type="number"
              value={materialNumber}
              onChange={(event) => setMaterialNumber(event.target.value)}
              disabled={submitting}
              fullWidth
            />

            <TextField
              label={t('beer.name')}
              variant="outlined"
              required
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={submitting}
              fullWidth
            />

            <Autocomplete
              disablePortal
              id="combo-brands"
              options={brandOptions}
              value={selectedBrand}
              disabled={submitting}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => {
                setBrandId(value?.id ?? '');
              }}
              noOptionsText="Brak marek"
              renderInput={(params) => (
                <TextField {...params} label={t('beer.brand')} required />
              )}
            />

            <Autocomplete
              disablePortal
              id="combo-packages"
              options={packageOptions}
              value={selectedPackage}
              disabled={submitting}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => {
                setPackageId(value?.id ?? '');
              }}
              noOptionsText="Brak opakowań"
              renderInput={(params) => (
                <TextField {...params} label={t('beer.package')} required />
              )}
            />
          </Stack>

          <Stack spacing={2}>
            <TextField
              label={t('beer.durability')}
              variant="outlined"
              type="number"
              required
              value={shelfLife}
              onChange={(event) => setShelfLife(event.target.value)}
              disabled={submitting}
              inputProps={{
                min: 0,
                step: 1,
              }}
              fullWidth
            />

            <TextField
              label={t('beer.quantity')}
              variant="outlined"
              type="number"
              required
              value={palletQty}
              onChange={(event) => setPalletQty(event.target.value)}
              disabled={submitting}
              inputProps={{
                min: 1,
                step: 1,
              }}
              helperText={
                kegType
                  ? 'Dla produktu KEG pełna paleta zawiera 6 KEG.'
                  : undefined
              }
              fullWidth
            />

            <TextField
              label={t('beer.hl')}
              variant="outlined"
              required
              value={casHL}
              onChange={(event) => setCasHL(event.target.value)}
              disabled={submitting}
              inputMode="decimal"
              fullWidth
            />

            <Button
              sx={{
                color: 'white',
                bgcolor: '#C09A53',
                ':hover': {
                  bgcolor: '#9c7c43',
                },
                height: '56px',
              }}
              type="submit"
              disabled={submitting}
              fullWidth
            >
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                buttonText
              )}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

function detectKegType(description) {
  const normalizedDescription = String(description ?? '')
    .toUpperCase()
    .replace(/\s+/g, '');

  if (/KEG20(?:L)?/.test(normalizedDescription)) {
    return 'KEG20';
  }

  if (/KEG30(?:L)?/.test(normalizedDescription)) {
    return 'KEG30';
  }

  if (/KEG50(?:L)?/.test(normalizedDescription)) {
    return 'KEG50';
  }

  return null;
}

function getKegStackQuantity(kegType) {
  const quantities = {
    KEG20: 24,
    KEG30: 18,
    KEG50: 12,
  };

  return quantities[kegType] ?? 6;
}

function getActionErrorMessage(error) {
  const message = error?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  if (typeof message === 'string') {
    return message;
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return 'Nie udało się zapisać produktu.';
}

export default BeerForm;
